import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";

function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    color: "success",
    message: "",
  });

  const loadGallery = async (
    page = pageNo,
    currentLimit = limit,
    searchValue = search,
    status = statusFilter
  ) => {
    if (init() !== "success") {
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(currentLimit),
      });

      if (searchValue.trim()) {
        params.append("search", searchValue.trim());
      }

      if (status) {
        params.append("status", status);
      }

      const response = await axios.get(`${Constants.getUrls.getAllGallery}?${params.toString()}`);
      const data = response?.data?.data;
      setItems(data?.docs || []);
      setPageNo(data?.page || page);
      setLimit(data?.limit || currentLimit);
      setTotalPages(data?.totalPages || 1);
      setTotalDocs(data?.totalDocs || 0);
    } catch (error) {
      setNotification({
        open: true,
        color: "error",
        message: error?.response?.data?.message || "Unable to load gallery items.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGallery(1, limit, search, statusFilter);
  }, []);

  const showMessage = (message, color = "success") => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, open: false, message: "" }));
    }, 2500);
  };

  const updateStatus = async (id, status) => {
    if (init() !== "success") {
      return;
    }

    setUpdatingId(id);

    try {
      const response = await axios.put(`${Constants.putUrls.updateGalleryStatus}${id}`, { status });
      showMessage(response?.data?.message || "Gallery status updated successfully.");
      loadGallery(pageNo, limit, search, statusFilter);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to update gallery status.", "error");
    } finally {
      setUpdatingId("");
    }
  };

  const deleteGalleryItem = async (id) => {
    if (!window.confirm("Delete this gallery item?")) {
      return;
    }

    try {
      const response = await axios.delete(`${Constants.deleteUrls.deleteGallery}${id}`);
      showMessage(response?.data?.message || "Gallery item deleted successfully.");
      loadGallery(pageNo, limit, search, statusFilter);
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to delete gallery item.", "error");
    }
  };

  const rows = items.map((item, index) => ({
    serial: (pageNo - 1) * limit + index + 1,
    gallery: (
      <MDBox>
        <MDTypography variant="button" fontWeight="medium">
          {item.title}
        </MDTypography>
        <MDTypography variant="caption" color="secondary" display="block">
          {item.caption || "No caption added yet."}
        </MDTypography>
      </MDBox>
    ),
    image: item.image ? (
      <img
        src={`${Constants.imageDomain}${item.image}`}
        alt={item.title}
        style={{ width: "160px", height: "110px", objectFit: "cover", borderRadius: "14px" }}
      />
    ) : (
      "-"
    ),
    order: item.sort_order ?? 0,
    status: (
      <FormControl fullWidth size="small">
        <InputLabel>Status</InputLabel>
        <Select
          value={item.status || "inactive"}
          label="Status"
          disabled={updatingId === item._id}
          onChange={(e) => updateStatus(item._id, e.target.value)}
          sx={{ minWidth: 140, backgroundColor: "#fff", borderRadius: "12px" }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
    ),
    created: new Date(item.createdAt).toLocaleDateString(),
    action: (
      <MDBox display="flex" gap={1}>
        <Link to={`/update-gallery/${item._id}`}>
          <MDButton variant="outlined" size="small" color="info">
            Edit
          </MDButton>
        </Link>
        <MDButton variant="outlined" size="small" color="error" onClick={() => deleteGalleryItem(item._id)}>
          Delete
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Gallery
            </MDTypography>
          </MDBox>

          <MDBox px={2} py={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Search gallery"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      loadGallery(1, limit, search, statusFilter);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => {
                      const value = e.target.value;
                      setStatusFilter(value);
                      loadGallery(1, limit, search, value);
                    }}
                    style={{ height: "45px", color: "#111" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4} textAlign={{ xs: "left", md: "right" }}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  sx={{ mr: 1 }}
                  onClick={() => loadGallery(1, limit, search, statusFilter)}
                >
                  Apply
                </MDButton>
                <Link to="/addgallery">
                  <MDButton variant="contained" color="primary">
                    Add Gallery Item
                  </MDButton>
                </Link>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox py={3}>
            {loading ? (
              <Grid align="center">
                <h4>Loading...</h4>
              </Grid>
            ) : rows.length > 0 ? (
              <DataTable
                table={{
                  columns: [
                    { Header: "#", accessor: "serial", width: "5%" },
                    { Header: "gallery", accessor: "gallery" },
                    { Header: "image", accessor: "image" },
                    { Header: "order", accessor: "order" },
                    { Header: "status", accessor: "status" },
                    { Header: "created", accessor: "created" },
                    { Header: "action", accessor: "action" },
                  ],
                  rows,
                }}
                entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
                canSearch={false}
                showTotalEntries
                pagination
                isSorted={false}
                noEndBorder={false}
                page_no={pageNo}
                totalPages={totalPages}
                totalDocs={totalDocs}
                onPageChange={(page) => loadGallery(page, limit, search, statusFilter)}
                onEntriesPerPageChange={(nextLimit) => {
                  setLimit(nextLimit);
                  loadGallery(1, nextLimit, search, statusFilter);
                }}
              />
            ) : (
              <MDBox px={3}>
                <MDTypography variant="button" color="secondary">
                  No gallery items found.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>

          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default Gallery;
